import {Component} from '@angular/core';
import {NgForOf, NgIf} from '@angular/common';
import {FormsModule} from '@angular/forms';
import * as XLSX from 'xlsx';
import {Project} from "../../interfaces/project";
import {AnnualSchoolProfile} from "../../interfaces/annual-school-profile";
import {ProjectService} from "../../services/project.service";
import {AnnualSchoolProfileService} from "../../services/annual-school-profile.service";
import {PartnerService} from "../../services/partner.service";
import {Partner} from "../../interfaces/partner";
import {SchoolLocations} from "../../data/locations";

@Component({
  selector: 'app-custom-form',
  standalone: true,
  imports: [NgForOf, NgIf, FormsModule],
  templateUrl: './custom-form.component.html',
  styleUrl: './custom-form.component.css'
})
export class CustomFormComponent {
  projects: Project[] = [];
  profiles: AnnualSchoolProfile[] = [];
  partners: Partner[] = [];

  constructor(
    private projectService: ProjectService,
    private annualSchoolProfileService: AnnualSchoolProfileService,
    private partnerService: PartnerService
  ) {
    this.loadData();
  }

  async loadData() {
    this.projects = await this.projectService.getProjects();
    this.profiles = await this.annualSchoolProfileService.getProfiles();
    this.partners = await this.partnerService.getPartners();
  }

  customFormData = {
    selectedRecord: '',
  };

  activeFilters = {
    years: false,
    school: false,
    cluster: false,
    goals: false,
    categories: false,
    grant: false,
    grades: false,
    partnerName: false,
    contactName: false,
    projectAreas: false,
    services: false,
    status: false,
    fundingSupport: false,
    feeOrFree: false,
    feeService: false
  };

  customAnnualData = {
    schoolYears: [] as string[],
    school: '',
    cluster: '',
    goals: [] as string[]
  };

  customProjectData = {
    schoolYears: [] as string[],
    school: '',
    cluster: '',
    categories: [] as string[],
    partnerContains: '',
    grant: '' as 'yes' | 'no' | '',
    grades: [] as string[]
  };

  customPartnerData = {
    partnerNameContains: '',
    contactNameContains: '',
    projectAreas: [] as string[],
    servicesOffered: [] as string[],
    partnerStatus: '',
    fundingSupport: [] as string[],
    feeOrFree: '',
    feeService: [] as string[]
  };

  getProjectAreas(): string[] {
    return Array.from(new Set(this.partners.flatMap(p => p.focusAreas || []))).sort();
  }

  getServicesOffered(): string[] {
    return Array.from(new Set(this.partners.flatMap(p => p.servicesOffered || []))).sort();
  }

  getFundingSupport(): string[] {
    return Array.from(new Set(this.partners.flatMap(p => p.fundingSupport || []))).sort();
  }

  getFeeServices(): string[] {
    return Array.from(new Set(this.partners.flatMap(p => p.feeServices || []))).sort();
  }

  getFilteredSchools(): string[] {
    const type = this.customFormData.selectedRecord;

    if (type === 'annual') {
      let data = this.profiles;

      if (this.customAnnualData.schoolYears.length) {
        data = data.filter(p =>
          this.customAnnualData.schoolYears.includes(p.schoolYear));
      }

      return Array.from(new Set(data.map(p => p.school))).sort();
    }

    if (type === 'project') {
      let data = this.projects;

      if (this.customProjectData.schoolYears.length) {
        data = data.filter(p =>
          this.customProjectData.schoolYears.includes(p.schoolYear));
      }

      return Array.from(new Set(data.map(p => p.school))).sort();
    }

    return [];
  }

  generateCustomExcel() {
    const type = this.customFormData.selectedRecord;
    let excelData: any[] = [];

    if (type === 'annual') {
      let data = [...this.profiles];
      if (this.customAnnualData.schoolYears.length) {
        data = data.filter(p => this.customAnnualData.schoolYears.includes(p.schoolYear));
      }
      if (this.customAnnualData.school) {
        data = data.filter(p => p.school === this.customAnnualData.school);
      }
      if (this.customAnnualData.cluster) {
        data = data.filter(p => {
          const cluster = SchoolLocations.find(loc => loc.SchoolName === p.school)?.Cluster;
          return cluster === this.customAnnualData.cluster;
        });
      }
      if (this.customAnnualData.goals.length) {
        data = data.filter(p => this.customAnnualData.goals.some(g => p.goals.includes(g)));
      }

      excelData = data.map(p => {
        const cluster = SchoolLocations.find(loc => loc.SchoolName === p.school)?.Cluster || 'Other';
        return {
          School: p.school,
          Cluster: cluster,
          Year: p.schoolYear,
          'City Limits': p.isWithinCityLimits,
          'Teacher Rep Name': p.ghsRepresentativeName,
          'Teacher Rep Email': p.ghsRepresentativeEmail,
          'Number of Students Enrolled in School': p.numOfStudents,
          'Number of teachers and Support Staff': p.numOfTeacherAndStaff,
          Goals: p.goals.join(', '),
          'Other goals commentary': p.goalsComment,
          'Created Date': p.submissionDate ? new Date(p.submissionDate).toISOString().split('T')[0] : '',
          'Created By': p.createdBy,
          'Updated Date': p.editedDate ? new Date(p.editedDate).toISOString().split('T')[0] : '',
          'Updated By': p.updatedBy,
          'Record ID': p.id
        };
      });

    } else if (type === 'project') {
      let data = [...this.projects];

      if (this.customProjectData.schoolYears.length) {
        data = data.filter(p => this.customProjectData.schoolYears.includes(p.schoolYear));
      }
      if (this.customProjectData.school) {
        data = data.filter(p => p.school === this.customProjectData.school);
      }
      if (this.customProjectData.cluster) {
        data = data.filter(p => {
          const schoolName = p.school.replace(/^\d{4}-\d{4}\s*-\s*/, '').trim();
          const cluster = SchoolLocations.find(loc => loc.SchoolName.toLowerCase() === schoolName.toLowerCase())?.Cluster;
          return cluster === this.customProjectData.cluster;
        });
      }
      if (this.customProjectData.categories.length) {
        data = data.filter(p => this.customProjectData.categories.some(cat => (p as any)[cat] === true));
      }
      if (this.customProjectData.grades.length) {
        data = data.filter(p => p.grades.some(g => this.customProjectData.grades.includes(g)));
      }
      if (this.customProjectData.grant.toLowerCase() === 'yes') {
        data = data.filter(p =>
          p.grants?.toLowerCase().trim() === 'yes'
        );
      } else if (this.customProjectData.grant.toLowerCase() === 'no') {
        data = data.filter(p =>
          p.grants?.toLowerCase().trim() === 'no'
        );
      }
      if (this.customProjectData.partnerContains) {
        data = data.filter(p => p.communityPartnersContactInfo?.toLowerCase().includes(this.customProjectData.partnerContains.toLowerCase()));
      }

      excelData = data.map(p => {
        const schoolName = p.school.replace(/^\d{4}-\d{4}\s*-\s*/, '').trim();
        const cluster = SchoolLocations.find(loc => loc.SchoolName.toLowerCase() === schoolName.toLowerCase())?.Cluster || 'Other';

        const categories = Object.keys(this.categoryFriendlyNames)
          .filter(key => (p as any)[key] === true)
          .map(key => this.categoryFriendlyNames[key]);

        return {
          Title: p.title,
          School: p.school,
          SchoolYear: p.schoolYear,
          Cluster: cluster,
          Grades: p.grades.join(', '),
          Partner: p.communityPartnersContactInfo,
          Grant: p.grants || '',
          ProjectStatus: p.status,
          Categories: categories.join(', ')
        };
      });

    } else if (type === 'partner') {
      let data = [...this.partners];

      if (this.customPartnerData.partnerNameContains) {
        data = data.filter(p => p.org.toLowerCase().includes(this.customPartnerData.partnerNameContains.toLowerCase()));
      }
      if (this.customPartnerData.contactNameContains) {
        data = data.filter(p => p.primeContact.toLowerCase().includes(this.customPartnerData.contactNameContains.toLowerCase()));
      }
      if (this.customPartnerData.projectAreas.length) {
        data = data.filter(p => p.focusAreas.some(a => this.customPartnerData.projectAreas.includes(a)));
      }
      if (this.customPartnerData.servicesOffered.length) {
        data = data.filter(p => p.servicesOffered.some(s => this.customPartnerData.servicesOffered.includes(s)));
      }
      if (this.customPartnerData.partnerStatus) {
        data = data.filter(p => this.customPartnerData.partnerStatus === p.status);
      }
      if (this.customPartnerData.fundingSupport.length) {
        data = data.filter(p =>
          (p.fundingSupport ?? []).some(a =>
            this.customPartnerData.fundingSupport.includes(a)
          )
        );
      }
      if (this.customPartnerData.feeOrFree) {
        data = data.filter(p =>
          p.feeType &&
          p.feeType.toLowerCase().trim() ===
          this.customPartnerData.feeOrFree.toLowerCase().trim()
        );
      }
      if (this.customPartnerData.feeService.length) {
        data = data.filter(p =>
          (p.feeServices ?? []).some(a =>
            this.customPartnerData.feeService.includes(a)
          )
        );
      }

      excelData = data.map(p => {
        return {
          Organization: p.org,
          'Prime Contact': p.primeContact,
          'Contact Email': p.contactEmail,
          'Contact Phone': p.contactPhone,
          'Areas Of Service': p.areaOfService,
          'Focus Areas': p.focusAreas.join(', '),
          'Services Offered': p.servicesOffered.join(', '),
          'Partner Status': p.status,
          'Funding Support': p.fundingSupport?.join(', ') || '',
          'Free/Fee Based': p.feeType,
          'Fee Services': p.feeServices?.join(', ') || '',
          'Created By': p.createdBy,
          'Partner ID': p.id
        };
      });
    }
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(excelData);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Custom Report');
    XLSX.writeFile(wb, `Custom_${type}_Report.xlsx`);
  }

  // YEARS
  getSchoolYears(): string[] {
    const type = this.customFormData.selectedRecord;
    if (type === 'annual') return Array.from(new Set(this.profiles.map(p => p.schoolYear))).sort();
    if (type === 'project') return Array.from(new Set(this.projects.map(p => p.schoolYear))).sort();
    return [];
  }

  isSelectedYear(y: string): boolean {
    return this.customFormData.selectedRecord === 'annual'
      ? this.customAnnualData.schoolYears.includes(y)
      : this.customProjectData.schoolYears.includes(y);
  }

  toggleYear(y: string, event: any) {
    const arr = this.customFormData.selectedRecord === 'annual'
      ? this.customAnnualData.schoolYears
      : this.customProjectData.schoolYears;

    if (event.target.checked) arr.push(y);
    else arr.splice(arr.indexOf(y), 1);
  }

  // GOALS
  getGoals(): string[] {
    let data = this.profiles;
    if (this.customAnnualData.schoolYears.length) {
      data = data.filter(p => this.customAnnualData.schoolYears.includes(p.schoolYear));
    }
    if (this.customAnnualData.school) {
      data = data.filter(p => p.school === this.customAnnualData.school);
    }
    if (this.customAnnualData.cluster) {
      data = data.filter(p => {
        const cluster = SchoolLocations.find(loc => loc.SchoolName === p.school)?.Cluster;
        return cluster === this.customAnnualData.cluster;
      });
    }

    const goals = data.flatMap(p => p.goals || []);
    return Array.from(new Set(goals)).sort();
  }

  isGoalSelected(g: string) {
    return this.customAnnualData.goals.includes(g);
  }

  toggleGoal(g: string, event: any) {
    if (event.target.checked) this.customAnnualData.goals.push(g);
    else this.customAnnualData.goals = this.customAnnualData.goals.filter(x => x !== g);
  }

  // CATEGORIES
  categoryFriendlyNames: Record<string, string> = {
    foodwasteToggle: 'Food Waste',
    educationalcampaign1: 'Educational Campaign',
    sharetable1: 'Share Table',
    compostingSelected: 'Composting',
    pantry: 'Food Pantry',
    fwasteaudit: 'Food Waste Audit',
    foodOtherCheck: 'Other',
    OutdoorToggle: 'Outdoor Learning Spaces',
    garden: 'Gardens',
    fish: 'Fish Hatchery',
    chicken: 'Chickens / Other Farm Animals',
    class: 'Outdoor Classroom / Gazebos',
    trails: 'Trails / Forested Area',
    greenhouse: 'Greenhouse(s)',
    compostSystem: 'Composting System',
    pollinatorCensus: 'Southeast Pollinator Census',
    outdoorOtherCheck: 'Other',
    IndoorToggle: 'Indoor Growing Systems',
    hydroponics1: 'Hydroponics',
    aquaponics1: 'Aquaponics',
    aeroponics1: 'Aeroponics',
    towers1: 'Grow Towers / Systems',
    indoorOtherCheck: 'Other',
    WaterToggle: 'Watershed Projects',
    waterEducationalCampaign: 'Water Educational Campaign',
    adoptARoadWatershed: 'Adopt-A-Road',
    adoptAStreamCleanup: 'Adopt-A-Stream Clean-up',
    adoptAStreamMonitoring: 'Adopt-A-Stream Monitoring',
    streamRestoration: 'Stream Restoration / Erosion Control',
    WatershedProjectsToggle: 'Other Community Watershed Projects',
    greatDaysService: 'GC&B Great Days of Service',
    wetlandCleanup: 'GC&B Wetland Cleanup',
    yellowRiverTrashBash: 'Yellow River Trash Bash',
    shoreSweep: 'Shore Sweep',
    localWaterEvent: 'Other Local Water Event',
    gwinnettServiceDept: 'Other Gwinnett County Service Dept.',
    supportCharity: 'Support Local Charity',
    studentProjects: 'Student / School Projects',
    watershedTripsToggle: 'Watershed Field Trips / In-house Programs',
    wasteWaterPlant: 'Waste Water Plant',
    watersheddrinkingWaterPlant: 'Drinking Water Plant',
    theWaterTower: 'The Water Tower',
    deptOfWaterResources: 'Dept of Water Resources',
    gwinnettCleanBeautiful: 'Gwinnett Clean & Beautiful',
    watershedOtherCheck: 'Other',
    RecyclingToggle: 'Recycling Items',
    paper1: 'Paper & Cardboard',
    textiles1: 'Textiles',
    plastics1: 'Plastics',
    aluminumcans1: 'Aluminum Cans',
    our: 'School Does Not Recycle',
    recycleOtherCheck: 'Other',
    RecyclingEventsToggle: 'Recycling Events',
    chipperEvent: 'Bring One for the Chipper',
    earthDayEvent: 'Earth Day',
    americaRecyclesEvent: 'America Recycles Day',
    recyclingEventsOtherCheck: 'Other',
    RenewableToggle: 'Renewable Energy',
    solar1: 'Solar',
    wind1: 'Wind',
    water1: 'Water',
  };

  getProjectCategoriesForForm(): string[] {
    let data = this.projects;

    if (this.customProjectData.schoolYears.length) {
      data = data.filter(p =>
        this.customProjectData.schoolYears.includes(p.schoolYear));
    }

    if (this.customProjectData.school) {
      data = data.filter(p =>
        p.school === this.customProjectData.school);
    }

    const ids = new Set<string>();

    data.forEach(p => {
      Object.keys(this.categoryFriendlyNames)
        .filter(key => (p as any)[key])
        .forEach(c => ids.add(c));
    });

    return Array.from(ids);
  }

  isCategorySelected(c: string) {
    return this.customProjectData.categories.includes(c);
  }

  toggleCategory(c: string, event: any) {
    if (event.target.checked) this.customProjectData.categories.push(c);
    else this.customProjectData.categories = this.customProjectData.categories.filter(x => x !== c);
  }

  getGrades(): string[] {
    const filtered = this.projects.filter(p =>
      !this.customProjectData.schoolYears.length || this.customProjectData.schoolYears.includes(p.schoolYear)
    );
    return Array.from(new Set(filtered.flatMap(p => p.grades))).sort();
  }

  isGradeSelected(g: string) {
    return this.customProjectData.grades.includes(g);
  }

  toggleGrade(g: string, event: any) {
    if (event.target.checked) this.customProjectData.grades.push(g);
    else this.customProjectData.grades = this.customProjectData.grades.filter(x => x !== g);
  }

  isProjectAreaSelected(p: string) {
    return this.customPartnerData.projectAreas.includes(p);
  }

  toggleProjectArea(p: string, event: any) {
    if (event.target.checked) this.customPartnerData.projectAreas.push(p);
    else this.customPartnerData.projectAreas = this.customPartnerData.projectAreas.filter(x => x !== p);

  }

  isServiceSelected(s: string) {
    return this.customPartnerData.servicesOffered.includes(s);
  }

  isFundingSelected(s: string) {
    return this.customPartnerData.fundingSupport.includes(s);
  }

  isFeeSelected(s: string) {
    return this.customPartnerData.feeService.includes(s);
  }

  toggleService(s: string, event: any) {
    if (event.target.checked) this.customPartnerData.servicesOffered.push(s);
    else this.customPartnerData.servicesOffered = this.customPartnerData.servicesOffered.filter(x => x !== s);
  }

  toggleFunding(s: string, event: any) {
    if (event.target.checked) this.customPartnerData.fundingSupport.push(s);
    else this.customPartnerData.fundingSupport = this.customPartnerData.fundingSupport.filter(x => x !== s);
  }

  toggleFeeService(s: string, event: any) {
    if (event.target.checked) this.customPartnerData.feeService.push(s);
    else this.customPartnerData.feeService = this.customPartnerData.feeService.filter(x => x !== s);
  }

  toggleFilter(key: keyof typeof this.activeFilters) {
    this.activeFilters[key] = !this.activeFilters[key];
    const type = this.customFormData.selectedRecord;

    //annual
    if (type === 'annual') {
      if (key === 'years' && !this.activeFilters.years)
        this.customAnnualData.schoolYears = [];
      if (key === 'school' && !this.activeFilters.school)
        this.customAnnualData.school = '';
      if (key === 'cluster' && !this.activeFilters.cluster)
        this.customAnnualData.cluster = '';
      if (key === 'goals' && !this.activeFilters.goals)
        this.customAnnualData.goals = [];
    }

    //project
    if (type === 'project') {
      if (key === 'years' && !this.activeFilters.years)
        this.customProjectData.schoolYears = [];
      if (key === 'school' && !this.activeFilters.school)
        this.customProjectData.school = '';
      if (key === 'cluster' && !this.activeFilters.cluster)
        this.customProjectData.cluster = '';
      if (key === 'categories' && !this.activeFilters.categories)
        this.customProjectData.categories = [];
      if (key === 'grades' && !this.activeFilters.grades)
        this.customProjectData.grades = [];
      if (key === 'grant' && !this.activeFilters.grant)
        this.customProjectData.grant = '';
      if (key === 'partnerName' && !this.activeFilters.partnerName)
        this.customProjectData.partnerContains = '';
    }

    //partner
    if (type === 'partner') {
      if (key === 'partnerName' && !this.activeFilters.partnerName)
        this.customPartnerData.partnerNameContains = '';
      if (key === 'contactName' && !this.activeFilters.contactName)
        this.customPartnerData.contactNameContains = '';
      if (key === 'projectAreas' && !this.activeFilters.projectAreas)
        this.customPartnerData.projectAreas = [];
      if (key === 'services' && !this.activeFilters.services)
        this.customPartnerData.servicesOffered = [];
      if (key === 'status' && !this.activeFilters.status)
        this.customPartnerData.partnerStatus = '';
      if (key === 'fundingSupport' && !this.activeFilters.fundingSupport)
        this.customPartnerData.fundingSupport = [];
      if (key === 'feeOrFree' && !this.activeFilters.feeOrFree)
        this.customPartnerData.feeOrFree = '';
      if (key === 'feeService' && !this.activeFilters.feeService)
        this.customPartnerData.feeService = [];
    }
  }

  onRecordChange() {
    Object.keys(this.activeFilters).forEach(k => this.activeFilters[k as keyof typeof this.activeFilters] = false);

    // Reset data
    this.customAnnualData = {schoolYears: [], school: '', cluster: '', goals: []};
    this.customProjectData = {
      schoolYears: [],
      school: '',
      cluster: '',
      categories: [],
      partnerContains: '',
      grant: '',
      grades: []
    };
    this.customPartnerData = {
      partnerNameContains: '',
      contactNameContains: '',
      projectAreas: [],
      servicesOffered: [],
      partnerStatus: '',
      fundingSupport: [],
      feeOrFree: '',
      feeService: []
    };
  }

  getClusters(): string[] {
    const type = this.customFormData.selectedRecord;

    if (type === 'annual') {
      let data = this.profiles;
      if (this.customAnnualData.schoolYears.length) {
        data = data.filter(p => this.customAnnualData.schoolYears.includes(p.schoolYear));
      }
      if (this.customAnnualData.school) {
        data = data.filter(p => p.school === this.customAnnualData.school);
      }
      if (this.customAnnualData.goals.length) {
        data = data.filter(p => this.customAnnualData.goals.some(g => p.goals.includes(g)));
      }

      const clusters = data
        .map(p => SchoolLocations.find(loc => loc.SchoolName === p.school)?.Cluster)
        .filter(c => !!c) as string[];

      return Array.from(new Set(clusters)).sort();
    }

    if (type === 'project') {
      let data = this.projects;

      if (this.customProjectData.schoolYears.length) {
        data = data.filter(p => this.customProjectData.schoolYears.includes(p.schoolYear));
      }
      if (this.customProjectData.school) {
        data = data.filter(p => p.school === this.customProjectData.school);
      }
      if (this.customProjectData.categories.length) {
        data = data.filter(p =>
          this.customProjectData.categories.some(catKey => (p as any)[catKey] === true)
        );
      }
      if (this.customProjectData.grades.length) {
        data = data.filter(p => p.grades.some(g => this.customProjectData.grades.includes(g)));
      }
      if (this.customProjectData.grant.toLowerCase() === 'yes') {
        data = data.filter(p =>
          p.grants?.toLowerCase().trim() === 'yes'
        );
      } else if (this.customProjectData.grant.toLowerCase() === 'no') {
        data = data.filter(p =>
          p.grants?.toLowerCase().trim() === 'no'
        );
      }
      if (this.customProjectData.partnerContains) {
        data = data.filter(p =>
          p.communityPartnersContactInfo?.toLowerCase().includes(this.customProjectData.partnerContains.toLowerCase())
        );
      }

      const clusters = data.map(p => {
        const schoolName = p.school.replace(/^\d{4}-\d{4}\s*-\s*/, '').trim();
        const loc = SchoolLocations.find(loc => loc.SchoolName.toLowerCase() === schoolName.toLowerCase());
        const cluster = loc?.Cluster || 'Other';
        return cluster;
      });
      return Array.from(new Set(clusters)).sort();
    }
    return [];
  }

  reset(){
    this.activeFilters.years = false;
    this.activeFilters.school = false;
    this.activeFilters.cluster = false;
    this.activeFilters.goals = false;
    this.activeFilters.categories = false;
    this.activeFilters.grant = false;
    this.activeFilters.grades = false;
    this.activeFilters.partnerName = false;
    this.activeFilters.contactName = false;
    this.activeFilters.projectAreas = false;
    this.activeFilters.services = false;
    this.activeFilters.status = false;
    this.activeFilters.fundingSupport = false;
    this.activeFilters.feeOrFree = false;
    this.activeFilters.feeService = false;

    if(this.customFormData.selectedRecord == 'annual'){
      this.customAnnualData.schoolYears = [];
      this.customAnnualData.school = '';
      this.customAnnualData.cluster = '';
      this.customAnnualData.goals = [];
      return;
    } if(this.customFormData.selectedRecord == 'project'){
      this.customProjectData.schoolYears = [];
      this.customProjectData.school = '';
      this.customProjectData.cluster = '';
      this.customProjectData.categories = [];
      this.customProjectData.partnerContains = '';
      this.customProjectData.grant = '';
      this.customProjectData.grades = [];
      return;
    }
    if(this.customFormData.selectedRecord == 'partner'){
      this.customPartnerData.partnerNameContains = '';
      this.customPartnerData.contactNameContains = '';
      this.customPartnerData.projectAreas = [];
      this.customPartnerData.servicesOffered = [];
      this.customPartnerData.partnerStatus = '';
      this.customPartnerData.fundingSupport = [];
      this.customPartnerData.feeOrFree = '';
      this.customPartnerData.feeService = [];
      return;
    }
  }

  protected readonly Array = Array;
}
